import { useLayoutEffect, useRef, type ReactElement } from "react";
import { Link } from "react-router-dom";
import { useAuthStore, selectUser, canAccessMenuSite, hasMenu, type MenuKey } from "@safers/auth";
import { cctvLivePath, controlPath, healthPath } from "@/routes/paths";

import type { SiteMode } from "./siteModes";

export type { SiteMode };

type IconProps = { className?: string };

function MonitorIcon({ className }: IconProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
    >
      <rect x="2.5" y="4" width="19" height="12.5" rx="2" strokeLinejoin="round" />
      <path d="M8.5 20.5h7M12 16.5v4" strokeLinecap="round" />
    </svg>
  );
}

function HealthIcon({ className }: IconProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        d="M20.4 5.9a5 5 0 0 0-7.1 0L12 7.2l-1.3-1.3a5 5 0 1 0-7.1 7.1l1.3 1.3L12 21.4l7.1-7.1 1.3-1.3a5 5 0 0 0 0-7.1Z"
        strokeLinejoin="round"
      />
      <path
        d="M3.5 12.5h3l1.5-2.5 2 5 2-4 1.5 1.5h3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CameraIcon({ className }: IconProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        d="M3 8.5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7Z"
        strokeLinejoin="round"
      />
      <path d="m15 11 5-3v8l-5-3v-2Z" strokeLinejoin="round" />
    </svg>
  );
}

const MODES: {
  key: SiteMode;
  label: string;
  menu: MenuKey;
  path: (id: string | null) => string;
  Icon: (p: IconProps) => ReactElement;
}[] = [
  {
    key: "site",
    label: "현장관제",
    menu: "CONTROL",
    path: (id) => controlPath(id),
    Icon: MonitorIcon,
  },
  {
    key: "health",
    label: "보건관리",
    menu: "HEALTH",
    path: (id) => healthPath(id),
    Icon: HealthIcon,
  },
  {
    key: "cctv",
    label: "AI CCTV",
    menu: "CCTV",
    path: (id) => cctvLivePath({ siteId: id }),
    Icon: CameraIcon,
  },
];

let lastMode: SiteMode | null = null;
let forgetTimer: ReturnType<typeof setTimeout> | undefined;
const reduceMotion = () =>
  typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;

interface SiteModeSwitcherProps {
  siteId?: string | number | null;
  current: SiteMode;
  variant?: "light" | "dark";
  layout?: "inline" | "bar";
  className?: string;
}

export function SiteModeSwitcher({
  siteId,
  current,
  variant = "light",
  layout = "inline",
  className = "",
}: SiteModeSwitcherProps) {
  const user = useAuthStore(selectUser);
  const id = siteId == null ? null : String(siteId);
  const numericId = id == null ? null : Number(id);
  const dark = variant === "dark";

  const allowed = (menu: MenuKey) =>
    numericId == null ? hasMenu(user, menu) : canAccessMenuSite(user, numericId, menu);

  const itemClass = (active: boolean, enabled: boolean) => {
    if (active) return dark ? "text-white" : "text-[var(--brand-primary-on-soft)]";
    if (!enabled)
      return dark ? "text-white/25 cursor-not-allowed" : "text-neutral-300 cursor-not-allowed";
    return dark ? "text-white/60 hover:text-white" : "text-neutral-600 hover:text-neutral-900";
  };

  const navRef = useRef<HTMLElement>(null);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);
  const pillRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    clearTimeout(forgetTimer);
    const items = itemRefs.current;
    const from = lastMode == null ? null : items[MODES.findIndex((m) => m.key === lastMode)];
    lastMode = current;

    const forget = () => {
      forgetTimer = setTimeout(() => {
        lastMode = null;
      }, 0);
    };

    const pill = pillRef.current;
    const to = items[MODES.findIndex((m) => m.key === current)];
    if (!pill || !to) return forget;

    const frame = (el: HTMLElement) => ({
      transform: `translateX(${el.offsetLeft}px)`,
      width: `${el.offsetWidth}px`,
    });
    const settle = () => Object.assign(pill.style, frame(to));

    settle();
    if (from && from !== to && !reduceMotion()) {
      pill.animate([frame(from), frame(to)], {
        duration: 200,
        easing: "cubic-bezier(0.32, 0.72, 0, 1)",
      });
    }

    const ro = new ResizeObserver(settle);
    if (navRef.current) ro.observe(navRef.current);
    return () => {
      ro.disconnect();
      forget();
    };
  }, [current]);

  const bar = layout === "bar";
  const shell = bar
    ? "gap-1.5"
    : `gap-0.5 rounded-xl border p-0.5 ${
        dark ? "border-white/10 bg-white/5" : "border-neutral-200 bg-neutral-100"
      }`;

  return (
    <nav
      ref={navRef}
      className={`relative flex items-center ${shell} ${className}`}
      aria-label="현장 화면 이동"
    >
      <span
        ref={pillRef}
        aria-hidden="true"
        className={`pointer-events-none absolute left-0 rounded-lg ${
          bar ? "inset-y-0" : "inset-y-0.5"
        } ${dark ? "bg-white/15" : "bg-white shadow-sm"}`}
      />
      {MODES.map((m, i) => {
        const active = m.key === current;
        const href = m.path(id);
        const enabled = allowed(m.menu);
        const cls = `relative flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg font-semibold transition-colors ${
          bar ? "flex-1 px-1.5 py-1.5 text-xs" : "px-2.5 py-1.5 text-[0.8125rem]"
        } ${itemClass(active, enabled)}`;
        const setRef = (el: HTMLElement | null) => {
          itemRefs.current[i] = el;
        };
        const body = (
          <>
            <m.Icon className={bar ? "h-4 w-4 flex-none" : "h-[1.125rem] w-[1.125rem]"} />
            <span className={bar ? "" : "hidden xl:inline"}>{m.label}</span>
          </>
        );

        if (!enabled || active) {
          return (
            <span
              key={m.key}
              ref={setRef}
              className={cls}
              aria-current={active ? "page" : undefined}
              title={enabled ? undefined : "해당 기능 권한이 없습니다"}
            >
              {body}
            </span>
          );
        }
        return (
          <Link key={m.key} ref={setRef} to={href} className={cls}>
            {body}
          </Link>
        );
      })}
    </nav>
  );
}
